import React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { cn } from 'lib/utils';
import { useSidebarStore } from 'store/sidebar.store';
import {
  LeftSidebar,
  NotificationBell,
  CommandPalette,
  HeaderProfile,
  HeaderTitle,
  MobileTabBar,
  HelpTourButton,
} from 'components/global';
import { GlobalUpgradeDialog, SubscriptionAlert } from 'components/entitlements';
import { SettingsDialog } from 'components/settings/SettingsDialog';
import { useUpgradeState } from 'modules/entitlements';
import * as AuthModule from 'modules/auth';
import { initSystemThemeListener } from 'store/theme.store';
import { useIsMobile } from 'hooks/use-media-query';
import { MobileShell } from 'components/mobile';
import { TelegramStartParamHandler } from 'components/TelegramStartParamHandler';
import { TelegramBackButton } from 'components/TelegramBackButton';
import { AwaitingAccessScreen } from 'components/global/AwaitingAccessScreen';
export const MainLayout: React.FC = () => {
    const location = useLocation();
    const { isOpen: isSidebarOpen, focusMode } = useSidebarStore();
    const isMobile = useIsMobile();
    // Run profile fetch so isFetched is set when settled; sidebar waits on isFetched to show menu items.
    AuthModule.Hooks.useProfile();
    const { state: authState } = AuthModule.Context.useAuthContext();
    // Listen for OS color-scheme changes when theme is "system"
    React.useEffect(() => initSystemThemeListener(), []);

    // The mobile Studio design is light-only, using the same app-blue accent as
    // the desktop's light theme. Reused chrome (SettingsDialog,
    // GlobalUpgradeDialog, CommandPalette, SubscriptionAlert) is mounted below
    // and portals to document.body, so on a dark-theme device it would render
    // dark and clash with the hardcoded-light phone screens in components/
    // mobile. Those Radix dialogs escape this component's DOM subtree, so a
    // wrapping className can't reach them — the only lever is the document root.
    // While the phone shell is mounted, force light mode; the light-theme
    // --primary is already the app ink (#171717) the mobile screens use, so no
    // token override is needed. Restored on unmount / when returning to the
    // desktop layout. Declared after the listener effect so it wins the initial
    // application.
    React.useEffect(() => {
      if (!isMobile) return;
      const root = document.documentElement;
      const hadDark = root.classList.contains('dark');
      const prevScheme = root.style.colorScheme;

      root.classList.remove('dark');
      root.style.colorScheme = 'light';

      return () => {
        if (hadDark) root.classList.add('dark');
        root.style.colorScheme = prevScheme;
      };
    }, [isMobile]);

    // Subscription/trial announcement bar spans the full width above BOTH the
    // sidebar and the page. It sits at the top of a vertical stack; the fixed
    // sidebar is pushed below it via the --banner-h custom property (0px when
    // the banner is hidden, so the offset is inert on every other surface).
    const { reason, bannerSeverity } = useUpgradeState();
    const showBanner = !focusMode && Boolean(bannerSeverity && reason);

    if (authState.isFetched && authState.role === 'NO_ACCESS') {
      return <AwaitingAccessScreen />;
    }

    // Phone shell (<768px): dedicated mobile screens + bottom dock, no sidebar
    // and no global top header (each screen owns its header). The shared dialogs
    // below still mount so ⌘K / upgrade / settings work everywhere.
    if (isMobile) {
      return (
        <div
          data-surface="studio"
          data-mobile-studio
          className="flex h-[100dvh] flex-col overflow-hidden bg-white text-[#111827]"
          // Pad below the status bar / notch once, at the shell root, so every
          // screen header and full-screen overlay sits clear of it. The white
          // background makes that inset strip read as an extension of the
          // (always-white) screen headers.
          style={{ paddingTop: 'env(safe-area-inset-top)' }}
        >
          {showBanner && <SubscriptionAlert />}
          <MobileShell focusMode={focusMode} />
          <CommandPalette />
          <GlobalUpgradeDialog />
          <SettingsDialog />
          {/* Telegram deep link (startapp=<code>) — renders nothing outside Telegram */}
          <TelegramStartParamHandler />
          {/* Native Telegram back chevron, wired to the router. Browser: no-op. */}
          <TelegramBackButton />
        </div>
      );
    }

    return (<div data-surface="studio" className="flex flex-col h-screen bg-background overflow-hidden" style={{ ['--banner-h' as string]: showBanner ? '2.25rem' : '0px', ['--header-h' as string]: '3.5rem' } as React.CSSProperties}>
      {/* Global announcement bar — full-width, above the sidebar + page */}
      {showBanner && <SubscriptionAlert />}

      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Left Sidebar */}
        <LeftSidebar />

        {/* Main content column — offset to clear the fixed sidebar. */}
        <div className={cn('flex-1 flex flex-col min-w-0 overflow-hidden transition-[margin] duration-300 ease-in-out', focusMode ? 'md:ml-0' : isSidebarOpen ? 'md:ml-[248px]' : 'md:ml-[68px]')}>
          {focusMode ? (
            // Focus mode: edge-to-edge workspace (owns its own header + scroll),
            // so no page card and no shell header.
            <main className="flex-1 overflow-hidden">
              <Outlet key={location.pathname} />
            </main>
          ) : (
            // Page card — aligned with sidebar on the left, with margin on top/right/bottom.
            <div className="my-2 mr-2 ml-0 flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-border bg-card shadow-sm md:my-3 md:mr-3 md:ml-0">
              <header className="flex h-14 shrink-0 items-center justify-between gap-3 px-4 lg:px-6">
                {/* Left: page context. */}
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <HeaderTitle />
                </div>

                {/* Right: help tour · notifications · account avatar. */}
                <div className="flex shrink-0 items-center gap-1">
                  <HelpTourButton />
                  <NotificationBell />
                  <HeaderProfile />
                </div>
              </header>

              {/* Scrollable page — key forces Outlet to re-render on route
                  change. Content is capped at 1280px and centered so it never
                  sprawls on ultrawide displays. */}
              <main className="flex-1 overflow-y-auto px-4 pb-6 pt-2 lg:px-6 lg:pb-8 scrollbar-thin">
                <div className="mx-auto h-full w-full max-w-[1280px]">
                  <Outlet key={location.pathname} />
                </div>
              </main>
            </div>
          )}
        </div>
      </div>

      {/* Thumb-first bottom navigation. Note: this branch only renders at
          >=768px (the phone shell above owns everything narrower), and
          MobileTabBar hides itself at that same 768px breakpoint post the
          tablet-shell-unification fix — so in the current wiring this never
          actually renders. Left mounted rather than removed here; see
          MobileTabBar's own doc comment. */}
      {!focusMode && <MobileTabBar />}

      {/* ⌘K command palette — mounted once; opened from the sidebar/header search
          triggers or the global shortcut (state in useCommandPaletteStore). */}
      <CommandPalette />

      {/* App-wide upgrade dialog — driven by entitlement 403s + the header badge */}
      <GlobalUpgradeDialog />

      {/* Settings dialog (?settings=<section>) — profile + org sections */}
      <SettingsDialog />

      {/* Telegram deep link (startapp=<code>) — renders nothing outside Telegram */}
      <TelegramStartParamHandler />
      {/* Native Telegram back chevron, wired to the router. Browser: no-op. */}
      <TelegramBackButton />
    </div>);
};
